import { z } from 'zod';

/* ───────────────────── Запрос КП (ЗК-01, ЗК-18, раздел 6.7) ───────────────────── */

/** Статусы запроса КП; «отменён» — служебный */
export const RFQ_STATUSES = {
  draft: 'Черновик',
  sent: 'Разослан',
  collecting: 'Приём ответов',
  compared: 'Сравнение готово',
  closed: 'Закрыт',
  cancelled: 'Отменён',
} as const;
export type RfqStatus = keyof typeof RFQ_STATUSES;
export const RfqStatusSchema = z.enum(['draft', 'sent', 'collecting', 'compared', 'closed', 'cancelled']);

/** Куда можно перейти из статуса (разослан → приём ответов происходит сам с первым ответом) */
export const RFQ_TRANSITIONS: Record<RfqStatus, RfqStatus[]> = {
  draft: ['sent', 'cancelled'],
  sent: ['collecting', 'cancelled'],
  collecting: ['compared', 'cancelled'],
  compared: ['collecting', 'closed', 'cancelled'],
  closed: [],
  cancelled: [],
};

const DateStr = z.iso.date();

export const RfqInput = z.object({
  /** Заявки на закуп: одна или несколько, позиции берутся из них */
  requestIds: z.array(z.uuid()).min(1).max(50),
  title: z.string().trim().min(2).max(300).optional(),
  responseDue: DateStr.nullable().optional(),
  deliveryAddress: z.string().trim().max(500).nullable().optional(),
  requirements: z.string().trim().max(4000).nullable().optional(),
  /** Категории ТМЦ и работ для подбора поставщиков из реестра контрагентов */
  categories: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
});
export type RfqInput = z.infer<typeof RfqInput>;

export const RfqPatch = RfqInput.omit({ requestIds: true }).partial();
export type RfqPatch = z.infer<typeof RfqPatch>;

export const RfqInvitesInput = z.object({
  supplierIds: z.array(z.uuid()).max(100).optional(),
  /** Добавить всех подходящих поставщиков по категориям запроса */
  byCategories: z.boolean().optional(),
});
export type RfqInvitesInput = z.infer<typeof RfqInvitesInput>;

export const RfqTransitionInput = z.object({
  to: RfqStatusSchema,
  reason: z.string().trim().max(1000).optional(),
});
export type RfqTransitionInput = z.infer<typeof RfqTransitionInput>;

export const RfqMessageInput = z.object({
  supplierId: z.uuid().nullable().optional(),
  kind: z.enum(['question', 'answer', 'note']).default('note'),
  body: z.string().trim().min(1).max(2000),
});
export type RfqMessageInput = z.infer<typeof RfqMessageInput>;

/** Ответ поставщика на запрос: цены по позициям запроса, остальное — как в КП заявки */
export const RfqResponseInput = z.object({
  supplierId: z.uuid(),
  receivedOn: DateStr.nullable().optional(),
  validUntil: DateStr.nullable().optional(),
  deliveryIncluded: z.boolean().optional(),
  deliveryCost: z.number().finite().nonnegative().nullable().optional(),
  deliveryDays: z.number().int().nonnegative().max(3650).nullable().optional(),
  paymentTerms: z.string().trim().max(300).nullable().optional(),
  advancePct: z.number().min(0).max(100).nullable().optional(),
  deferralDays: z.number().int().min(0).max(365).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  /** Цена за единицу с НДС по позиции запроса (ключ — id позиции запроса); null — позиции нет */
  prices: z.record(z.uuid(), z.number().finite().nonnegative().nullable()),
});
export type RfqResponseInput = z.infer<typeof RfqResponseInput>;

export const RfqDeclineInput = z.object({
  supplierId: z.uuid(),
  /** Отказ — всегда с причиной (ЗК-32) */
  reason: z.string().trim().min(3, 'Укажите причину отказа').max(500),
});

/** Статус участия приглашённого поставщика (ЗК-32), от раннего к позднему; «отказался» и «КП подано» — конечные */
export const RFQ_INVITE_STATUSES = [
  'draft',
  'sent',
  'opened',
  'downloaded',
  'will_participate',
  'declined',
  'submitted',
] as const;
export const RfqInviteStatusSchema = z.enum(RFQ_INVITE_STATUSES);
export type RfqInviteStatus = z.infer<typeof RfqInviteStatusSchema>;
export const RFQ_INVITE_STATUS_LABEL: Record<RfqInviteStatus, string> = {
  draft: 'в черновике',
  sent: 'приглашение отправлено',
  opened: 'приглашение открыто',
  downloaded: 'документы скачаны',
  will_participate: 'будет участвовать',
  declined: 'отказался',
  submitted: 'КП подано',
};

/** Отметка шага участия со слов поставщика (пока нет кабинета поставщика — вносит закупщик) */
export const RfqInviteStepInput = z.object({
  step: z.enum(['opened', 'downloaded', 'will_participate']),
});
export type RfqInviteStepInput = z.infer<typeof RfqInviteStepInput>;

/** Статус по отметкам: КП подано сильнее отказа, затем идут шаги участия */
export function rfqInviteStatus(i: {
  sentAt: string | null;
  respondedAt: string | null;
  declinedAt: string | null;
  openedAt: string | null;
  downloadedAt: string | null;
  willParticipateAt: string | null;
}): RfqInviteStatus {
  if (i.respondedAt) return 'submitted';
  if (i.declinedAt) return 'declined';
  if (i.willParticipateAt) return 'will_participate';
  if (i.downloadedAt) return 'downloaded';
  if (i.openedAt) return 'opened';
  return i.sentAt ? 'sent' : 'draft';
}

/** Статус разбора файла ответа поставщика (ЗК-19) */
export const RFQ_OFFER_FILE_STATUSES = {
  parsed_draft: 'Черновик из файла',
  pending_review: 'На проверке снабженцем',
  confirmed: 'Подтверждено',
  failed: 'Не разобрано',
} as const;
export type RfqOfferFileStatus = keyof typeof RFQ_OFFER_FILE_STATUSES;
export const RfqOfferFileStatusSchema = z.enum(['parsed_draft', 'pending_review', 'confirmed', 'failed']);

export const RfqOfferFileInfo = z.object({
  id: z.uuid(),
  fileId: z.uuid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  kind: z.enum(['excel', 'pdf', 'other']),
  parseStatus: RfqOfferFileStatusSchema,
  parseNote: z.string().nullable(),
  /** Черновик цен по id позиции запроса; суммы — только при уровне денег */
  draftPrices: z.record(z.uuid(), z.number().finite().nonnegative().nullable()).optional(),
  draftDeliveryDays: z.number().int().nonnegative().nullable(),
  supplierId: z.uuid(),
  supplierName: z.string().optional(),
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
});
export type RfqOfferFileInfo = z.infer<typeof RfqOfferFileInfo>;

/** Привязка уже загруженного через /files файла как ответа на приглашение */
export const SupplierRfqOfferFileInput = z.object({
  fileId: z.uuid(),
});
export type SupplierRfqOfferFileInput = z.infer<typeof SupplierRfqOfferFileInput>;

/** Снабженец подтверждает или правит позиции из файла */
export const RfqOfferFileConfirmInput = z.object({
  prices: z.record(z.uuid(), z.number().finite().nonnegative().nullable()).optional(),
  deliveryDays: z.number().int().nonnegative().max(3650).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type RfqOfferFileConfirmInput = z.infer<typeof RfqOfferFileConfirmInput>;

export const RfqSummary = z.object({
  id: z.uuid(),
  number: z.string(),
  title: z.string(),
  status: RfqStatusSchema,
  statusChangedAt: z.string(),
  responseDue: DateStr.nullable(),
  overdue: z.boolean(),
  requests: z.number().int(),
  lines: z.number().int(),
  invited: z.number().int(),
  responded: z.number().int(),
});
export type RfqSummary = z.infer<typeof RfqSummary>;

export const RfqDetail = RfqSummary.extend({
  deliveryAddress: z.string().nullable(),
  requirements: z.string().nullable(),
  categories: z.array(z.string()),
  cancelReason: z.string().nullable(),
  sentAt: z.string().nullable(),
  requestList: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      title: z.string(),
      projectCode: z.string(),
      stage: z.string(),
    }),
  ),
  lineList: z.array(
    z.object({
      id: z.uuid(),
      requestLineId: z.uuid(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number(),
    }),
  ),
  inviteList: z.array(
    z.object({
      supplierId: z.uuid(),
      supplierName: z.string(),
      stopListed: z.boolean(),
      sentAt: z.string().nullable(),
      respondedAt: z.string().nullable(),
      declinedAt: z.string().nullable(),
      declineReason: z.string().nullable(),
      openedAt: z.string().nullable(),
      downloadedAt: z.string().nullable(),
      willParticipateAt: z.string().nullable(),
      status: RfqInviteStatusSchema,
    }),
  ),
  messages: z.array(
    z.object({
      id: z.uuid(),
      supplierId: z.uuid().nullable(),
      supplierName: z.string().nullable(),
      kind: z.enum(['question', 'answer', 'note']),
      body: z.string(),
      at: z.string(),
    }),
  ),
  /** Ссылки на КП, пришедшие по запросу (цены смотрят в заявке — по уровню денег) */
  offerList: z.array(z.object({ id: z.uuid(), requestId: z.uuid(), supplierId: z.uuid() })),
  /** Файлы ответов поставщиков (ЗК-19); цены черновика — по уровню денег */
  offerFiles: z.array(RfqOfferFileInfo).default([]),
  nextStatuses: z.array(RfqStatusSchema),
});
export type RfqDetail = z.infer<typeof RfqDetail>;

/**
 * ЗКП-2026-012 (ЗК-02): год создания запроса и сквозной номер в компании. Номер не начинается заново с годом —
 * так он остаётся единственным и в старых ссылках; год показывает, когда запрос заведён.
 */
export function rfqNumber(n: number, createdAt: string): string {
  // Год — по времени компании (Алматы, как в поиске и отчётах), а не по UTC: запрос 1 января ночью — уже новый год
  const year = new Intl.DateTimeFormat('en', { timeZone: 'Asia/Almaty', year: 'numeric' }).format(
    new Date(createdAt),
  );
  return `ЗКП-${year}-${String(n).padStart(3, '0')}`;
}

/* ───────────────────── Кабинет поставщика: ответ на запрос КП (ВК-06, ЗК-18) ───────────────────── */

/** Ответ внешнего поставщика: поставщик берётся из членства, не из тела */
export const SupplierRfqReplyInput = RfqResponseInput.omit({ supplierId: true });
export type SupplierRfqReplyInput = z.infer<typeof SupplierRfqReplyInput>;

export const SupplierRfqInviteSummary = z.object({
  id: z.uuid(),
  number: z.string(),
  title: z.string(),
  status: RfqStatusSchema,
  responseDue: DateStr.nullable(),
  overdue: z.boolean(),
  lines: z.number().int(),
  sentAt: z.string().nullable(),
  respondedAt: z.string().nullable(),
  declinedAt: z.string().nullable(),
  /** Можно ещё ответить или отказаться */
  open: z.boolean(),
});
export type SupplierRfqInviteSummary = z.infer<typeof SupplierRfqInviteSummary>;

export const SupplierRfqInviteDetail = SupplierRfqInviteSummary.extend({
  deliveryAddress: z.string().nullable(),
  requirements: z.string().nullable(),
  lineList: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number(),
      /** Уже внесённая цена за единицу (своё КП); null — позиции нет в ответе */
      price: z.number().nullable().optional(),
    }),
  ),
  /** Условия из уже внесённого КП, если отвечал раньше */
  previous: z
    .object({
      deliveryDays: z.number().nullable(),
      deliveryIncluded: z.boolean(),
      deliveryCost: z.number().nullable(),
      paymentTerms: z.string().nullable(),
      advancePct: z.number().nullable(),
      deferralDays: z.number().nullable(),
      note: z.string().nullable(),
      validUntil: DateStr.nullable(),
    })
    .nullable(),
  messages: z.array(
    z.object({
      id: z.uuid(),
      kind: z.enum(['question', 'answer', 'note']),
      body: z.string(),
      at: z.string(),
    }),
  ),
  stopListed: z.boolean(),
  /** Загруженные Excel/PDF ответы (ЗК-19) */
  offerFiles: z.array(RfqOfferFileInfo).default([]),
});
export type SupplierRfqInviteDetail = z.infer<typeof SupplierRfqInviteDetail>;
